import { defineStore } from "pinia";
import { FormValues } from "./types.d";
import { EntityClass, EntityTypeName } from "@/data/types/types.gen.d";
import { type JSONData } from "@/data/base_classes/dto.d";
import { formValuesToJSONData, JSONDataToFormValues } from "./functions";

interface FormStore {
  formValues: FormValues;
  originalFormValues: FormValues;
  entityTypeName: EntityTypeName | null;
  entity: EntityClass | null;
  preprocess?: (data: JSONData) => Promise<JSONData>;
  process?: (data: JSONData) => Promise<JSONData>;
  tab: string;
  tabs: string[];
}

const router = useRouter();
export const useFormStore = defineStore("formStore", {
  state: (): FormStore => ({
    formValues: {},
    originalFormValues: {},
    entityTypeName: null,
    entity: null,
    tab: "",
    tabs: [],
  }),
  actions: {
    async build(
      entityTypeName: EntityTypeName,
      entity: EntityClass,
      preprocess: FormStore["preprocess"],
      process: FormStore["process"],
      tabs: string[],
    ): Promise<void> {
      this.entityTypeName = entityTypeName;
      this.entity = entity;
      this.preprocess = preprocess;
      this.process = process;

      // Set form values
      await this.fromJSONData(entity.toJSON());

      // Merge from query
      this.mergeFromQuery();

      // Set tabs
      this.tabs = tabs;
    },
    set(key: string, value: any): void {
      this.formValues[key] = value;
    },
    clear(): void {
      this.formValues = {};
      this.entityTypeName = null;
      this.entity = null;
    },
    async fromJSONData(data: JSONData): Promise<void> {
      if (this.preprocess) {
        this.formValues = JSONDataToFormValues(await this.preprocess(data));
      } else {
        this.formValues = JSONDataToFormValues(data);
      }
    },
    async toJSONData(): Promise<JSONData> {
      const data = formValuesToJSONData(this.formValues);

      // Do not include id
      delete data.id;

      if (this.process) {
        return this.process(data);
      } else {
        return data;
      }
    },
    mergeFromQuery(): void {
      const query = router?.currentRoute?.value?.query;
      if (query?.formValues) {
        const queryFormValues = JSON.parse(query.formValues as string);

        // Merge the formValues with the current formValues
        Object.keys(queryFormValues).forEach((key) => {
          this.formValues[key] = queryFormValues[key];
        });

        // Save the original formValues
        this.originalFormValues = JSON.parse(JSON.stringify(this.formValues));

        // Remove the formValues from the query
        setTimeout(() => {
          router.push({ query: { ...query, formValues: undefined } });
        }, 500);
      }
    },
  },
});

// Export a function to create a new instance of the store
export function createFormStore() {
  return useFormStore();
}
